import { forwardRef, useEffect, useRef, useState } from "react";
import { Footer } from "../Footer/Footer";
import { Outlet } from "react-router-dom";
import Header from "@components/Header/Header";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "@store/store";
import type { AppDispatch } from "@store/store";
import useNotifications from "@features/Notification/useNotifications";
import Sidebar from "@components/Sidebar/Sidebar";
import ModalManager from "@components/common/ModalManager/ModalManager";
import SessionStatusChecker from "@components/common/SessionStatusChecker/SessionStatusChecker";
import RoutePermissionGuard from "@components/RouteGuards/RoutePermissionGuard";
import { PageToolbarProvider } from "@providers/PageToolbarProvider";
import { useProfile } from "@features/Profile/services/GetProfile";
import { openModal, closeModal } from "@store/slices/modalSlice";
import { MODAL_KEYS } from "@constants/global.constants";
import { useTranslation } from "react-i18next";
import { usePortalSourceWp } from "@providers/PortalSourceWpProvider";
import "@components/Sidebar/Sidebar.css";
import { getInitialSidebarCollapsed, isCmAdminUser } from "@features/CmDashboard/cmAdmin";
const CM_MOBILE_MEDIA_QUERY = "(max-width: 767px)";

function useMediaQuery(query: string): boolean {
	const [matches, setMatches] = useState(() => {
		if (typeof window === "undefined") return false;
		return window.matchMedia(query).matches;
	});

	useEffect(() => {
		const mediaQuery = window.matchMedia(query);
		const handleChange = () => setMatches(mediaQuery.matches);
		handleChange();
		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, [query]);

	return matches;
}

export const MainLayout = forwardRef<HTMLElement>(() => {
	const dispatch = useDispatch<AppDispatch>();
	const { t } = useTranslation();
	const isPortalSourceWp = usePortalSourceWp();
	const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
	const user = useSelector((state: RootState) => state.user.user);
	const userEmail = user?.emailAddress;
	const isCmAdmin = isCmAdminUser(user);
	const isCmMobile = useMediaQuery(CM_MOBILE_MEDIA_QUERY);
	const hideCmSidebar = isCmAdmin && isCmMobile;
	const isProfileModalOpen = useSelector((state: RootState) => state.modal.profileModal.isOpen);
	const { fetchInitial } = useNotifications();
	const fetchedRef = useRef(false);

	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => getInitialSidebarCollapsed(user));

	// Listen for sidebar toggle events
	useEffect(() => {
		const handleSidebarToggle = (event: CustomEvent<{ isCollapsed: boolean }>) => {
			setIsSidebarCollapsed(event.detail.isCollapsed);
		};

		window.addEventListener("sidebarToggle", handleSidebarToggle as EventListener);
		return () => {
			window.removeEventListener("sidebarToggle", handleSidebarToggle as EventListener);
		};
	}, []);

	// Fetch user profile to check signature status
	const { data: profileData } = useProfile();

	useEffect(() => {
		if (isCmAdmin) return;
		if (isAuthenticated && !fetchedRef.current) {
			fetchInitial();
			fetchedRef.current = true;
		}
	}, [isAuthenticated, fetchInitial, isCmAdmin]);

	// Check if signature is not set - check the actual signature field, not isSignatureSet
	const showSignatureRequired = Boolean(isAuthenticated && profileData && !profileData.signature);

	// Drive signature-required CommonModal via ModalManager (hide while profile modal is open)
	useEffect(() => {
		const shouldShowSignaturePrompt = showSignatureRequired && !isProfileModalOpen;
		if (shouldShowSignaturePrompt) {
			dispatch(openModal(MODAL_KEYS.SIGNATURE_REQUIRED));
		} else {
			dispatch(closeModal(MODAL_KEYS.SIGNATURE_REQUIRED));
		}
	}, [showSignatureRequired, isProfileModalOpen, dispatch]);

	// Update document title based on user email (WP external entry overrides)
	useEffect(() => {
		if (isPortalSourceWp) {
			document.title = t("psdaCommon.auth.pageTitle.authorizedPortal");
			return;
		}
		if (isCmAdmin) {
			document.title = "CM Dashboard";
		} else if (userEmail === "tevta@gmail.com") {
			document.title = "TEVTA Portal";
		} else if (userEmail === "pvtc@gmail.com") {
			document.title = "PVTC Portal";
		} else {
			document.title = "Admin Portal";
		}
	}, [userEmail, isPortalSourceWp, t, isCmAdmin]);

	return (
		<div className={`flex min-h-screen flex-col bg-background ${isCmAdmin ? "cm-admin-layout" : ""}`}>
			<Header />
			{hideCmSidebar ? null : <Sidebar />}
			<div
				className={`flex-1 main-content ${
					hideCmSidebar
						? "main-content-cm-mobile"
						: isSidebarCollapsed
							? "main-content-collapsed"
							: "main-content-expanded"
				}`}>
				<main className="page-content w-full min-w-0 p-4 sm:p-6">
					<RoutePermissionGuard>
						<PageToolbarProvider>
							<Outlet />
						</PageToolbarProvider>
					</RoutePermissionGuard>
				</main>
			</div>
			<Footer />
			<ModalManager />
			<SessionStatusChecker />
		</div>
	);
});
